import type {
  ApiError,
  ResourceRecord,
  SdkApiError,
  SdkRecordMutationState
} from '@Pages/pageRuntime/sdk/types';
import { useMutation, type MutationFunction } from '@tanstack/react-query';

type RecordMutationOptions = {
  onSuccess?: (record: ResourceRecord) => void;
  onError?: (error: SdkApiError) => void;
};

export type RecordMutationHandle<TVariables> = SdkRecordMutationState & {
  /**
   * Runs the mutation. Resolves with the record on success and `null` on failure —
   * it never rejects, so a page calling it straight from an `onClick` without
   * awaiting cannot produce an unhandled rejection. The failure lands on `error`.
   */
  run: (variables: TVariables) => Promise<ResourceRecord | null>;
};

/**
 * Shared plumbing behind the reactive record write hooks (`useCreateRecord`,
 * `useUpdateRecord`). Those differ only in the request they fire and the name they
 * give `run`; the state shape and the never-rejects contract live here so the two
 * cannot drift apart.
 */
export function useRecordMutation<TVariables>(
  mutationFn: MutationFunction<ResourceRecord, TVariables>,
  options?: RecordMutationOptions
): RecordMutationHandle<TVariables> {
  const mutation = useMutation<ResourceRecord, ApiError, TVariables>({
    mutationFn,
    onSuccess: (record) => options?.onSuccess?.(record),
    onError: (error) => options?.onError?.(error)
  });

  return {
    run: (variables) =>
      mutation.mutateAsync(variables).then(
        (record) => record ?? null,
        () => null
      ),
    data: mutation.data,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: () => {
      mutation.reset();
    }
  };
}
